import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import moment from "moment";
import LottieLoader from '../../LottieLoader';
import lottieLoaderFile from '../../../files/animation/loading.json';
import RPMCarePlanForm from './RPMCarePlanForm';
import RPMCarePlanPrint from './RPMCarePlanPrint';
import { is } from "date-fns/locale";
const _ = require("lodash");

const ViewRPMCarePlan = ({ onCancel, profile, rpmCarePlanId, isEditing, onSaveComplete, userId }, ref) => {
  const dispatch = useDispatch();
  const saveRef = useRef(null);
  const printRef = useRef(null);
  
  // Get RPM care plan info from Redux store
  const { rpmCarePlanInfo, rpmCarePlanDetailsInfo, ongoingRPMCarePlanRequest, ongoingRPMCarePlanDetailsRequest, rpmPatientConditions, rpmPatientExpectations = [], rpmPatientAssignedStaff = [], rpmAvailableStaff = [], rpmMedicalNecessityConfigFromRedux = [] } = useSelector(
    (state) => {
      return {
        rpmCarePlanInfo: state.careprovider.rpmCarePlanInfo,
        rpmCarePlanDetailsInfo: state.careprovider.rpmCarePlanDetailsInfo,
        ongoingRPMCarePlanRequest: state.careprovider.ongoingRPMCarePlanRequest,
        ongoingRPMCarePlanDetailsRequest: state.careprovider.ongoingRPMCarePlanDetailsRequest,
        rpmPatientConditions: state.careprovider.rpmPatientConditions || [],
        rpmPatientExpectations: state.careprovider.rpmPatientExpectations || [],
        rpmPatientAssignedStaff: state.careprovider.rpmPatientAssignedStaff || [],
        rpmAvailableStaff: state.careprovider.rpmAvailableStaff || [],
        rpmMedicalNecessityConfigFromRedux: state.careprovider.rpmMedicalNecessityConfig || []
      };
    }
  );
    
  // Use userId as patientId for the API call
  const patientId = userId || profile?._id;
  
  // Clear Redux cache when component unmounts (popup closes)
  useEffect(() => {
    return () => {
      dispatch({ type: "CLEAR_RPM_MEDICAL_NECESSITY_CONFIG" });
    };
  }, [dispatch]);

  const [formData, setFormData] = useState({
    patientName: "",
    dob: "",
    primaryDiagnosis: "",
    orderingProvider: "",
    npi: "",
    dateRPMInitiated: "",
    lastModifiedDate: "",
    setupEducationDate: "",
    medicalNecessity: "",
    rpmRationale: "",
    consentDate: "",
    deviceInfo: "",
    goals: "",
    careTeam: "",
    escalationPlan: {},
    goalsOfCare: {},
    patientSignature: null,
    providerSignature: null,
    providerName: "",
    showConditionError: false
  });

  const [selectedConditionIds, setSelectedConditionIds] = useState([]);
  const [selectedConditionIcdCodes, setSelectedConditionIcdCodes] = useState([]);
  const [selectedExpectations, setSelectedExpectations] = useState([]);
  const [showIcdCodesError, setShowIcdCodesError] = useState(false);
  const [isIcdCodesValid, setIsIcdCodesValid] = useState(true);
  const carePlanFormRef = useRef(null);
  const [conditionNames, setConditionNames] = useState([]);
  const [rpmMedicalNecessityConfig, setRpmMedicalNecessityConfig] = useState([]);

  // Expose save function and formData to parent via ref
  React.useImperativeHandle(ref, () => ({
    save: () => {
      return handleSave();
    },
    getFormData: () => {
      // Return formData if it has meaningful data, otherwise return default object
      const defaultData = {
        patientName: "",
        dob: "",
        primaryDiagnosis: "",
        orderingProvider: "",
        npi: "",
        dateRPMInitiated: "",
        medicalNecessity: false,
        rpmRationale: false,
        consentDate: "",
        deviceInfo: "",
        careTeam: {},
        escalationPlan: {},
        goalsOfCare: {},
        patientSignature: null,
        providerSignature: null,
        providerName: ""
      };
      
      // Check if formData has meaningful data
      if (formData && (formData.patientName || formData.primaryDiagnosis || formData.orderingProvider)) {
        return formData;
      }
      return defaultData;
    },
    getPrintRef: () => printRef.current
  }));

  // Trigger DO_GET_RPM_CARE_PLAN_INFORMATION when isEditing becomes true
  useEffect(() => {
    if (isEditing && patientId) {
      dispatch({
        type: "DO_GET_RPM_CARE_PLAN_INFORMATION",
        payload: { patientId: patientId }
      });
      dispatch({
        type: "DO_GET_RPM_PATIENT_CONDITIONS",
        payload: { patientId }
      });
    }
  }, [isEditing, patientId, dispatch]);

  // Auto-fetch data when component opens
  useEffect(() => {
    if (rpmCarePlanId) {
      dispatch({
        type: "DO_GET_RPM_CARE_PLAN_DETAILS",
        payload: { rpmCarePlanId }
      });
    }
    // Fetch patient conditions for matching savedConditionIds in form
    if (patientId) {
      dispatch({
        type: "DO_GET_RPM_PATIENT_CONDITIONS",
        payload: { patientId }
      });
    }
    // Also fetch RPM staff members so we can resolve saved designated staff IDs to names
    if (patientId) {
      dispatch({ type: "DO_GET_RPM_STAFF_MEMBERS", payload: { patientId } });
    }
    // Also fetch global available staff (no patientId) to improve name resolution
    dispatch({ type: "DO_GET_RPM_STAFF_MEMBERS", payload: {} });
  }, [rpmCarePlanId, patientId, dispatch]);

  useEffect(() => {
    if (conditionNames.length > 0) {
      setFormData(prev => ({
        ...prev,
        conditionNames: conditionNames
      }));
    }
  }, [conditionNames]);
  
// Handle save - call the update API
  const handleSave = () => {
    const data = isEditing ? rpmCarePlanInfo : rpmCarePlanDetailsInfo;
    
    // Validation: check if conditions are selected
    if (!selectedConditionIds || selectedConditionIds.length === 0) {
      setFormData(prev => ({ ...prev, showConditionError: true }));
      return false;
    }

    // Validation: block save if any ICD-10 diagnosis row is incomplete (e.g. category
    // picked but no code chosen) — otherwise it would silently be dropped from the save.
    if (!isIcdCodesValid) {
      setShowIcdCodesError(true);
      if (carePlanFormRef.current?.scrollToIcdCodesError) {
        carePlanFormRef.current.scrollToIcdCodesError();
      }
      return false;
    }
    setShowIcdCodesError(false);

    if (rpmCarePlanId) {
      // Prepare data in the format expected by the API, using original objects and updating with form data
      const apiData = {
        patientId: patientId,
        userProfile: {
          mainCareProvider: rpmCarePlanInfo?.userProfile?.mainCareProviderId,
          primaryDiagnosis: rpmCarePlanInfo?.userProfile?.primaryDiagnosis,
          careCoordinator: rpmCarePlanInfo?.userProfile?.careCoordinator,
          NPI: rpmCarePlanInfo?.userProfile?.NPI,
        },
        patientConsent: {
          rpmVerbalNoteDate: rpmCarePlanInfo?.patientConsent?.rpmVerbalNoteDate
        },
        patientExpectations: {
          medicalNecessity: rpmCarePlanInfo?.patientExpectations?.medicalNecessity || false,
          rationaleRPM: rpmCarePlanInfo?.patientExpectations?.rationaleRPM || false,
          expectationThreshold: rpmPatientExpectations.length > 0 ? rpmPatientExpectations : selectedExpectations
        },
        devices: rpmCarePlanInfo?.devices?.map(d => ({
          device: d.device?._id,
          serial: d.serial
        })) || [],
        careTeam: {
          ...rpmCarePlanInfo?.careTeam
        },
        escalationPlan: rpmCarePlanInfo?.escalationPlan._id,
        goalsOfCare: rpmCarePlanInfo?.goalsOfCare._id,
        // Include the new condition and expectations fields
        conditionIds: selectedConditionIds,
        // Include the selected ICD-10 diagnosis codes (category -> code, or manual entry)
        conditionIcdCodes: selectedConditionIcdCodes,
        // Include RPM medical necessity config IDs (use local state from API data, fallback to Redux)
        rpmMedicalNecessity: rpmMedicalNecessityConfig.length > 0 
          ? rpmMedicalNecessityConfig.map(config => config._id || config)
          : (rpmMedicalNecessityConfigFromRedux.length > 0 
            ? rpmMedicalNecessityConfigFromRedux.map(config => config._id)
            : (data.rpmMedicalNecessity || []))
      };
      dispatch({
        type: "DO_UPDATE_RPM_CARE_PLAN",
        payload: {
          rpmCarePlanId: rpmCarePlanId,
          formData: apiData
        }
      });
      return true;
    }
    
    return false;
  };

  // Update form data when API response is received
  useEffect(() => {
    if (rpmCarePlanDetailsInfo || rpmCarePlanInfo) {
      const data = isEditing ? rpmCarePlanInfo : rpmCarePlanDetailsInfo;
      if(!data) return; // safety check 
      const userProfile = data.userProfile || {};
      const patientConsent = data.patientConsent || {};
      const patientExpectations = data.patientExpectations || {};
      const devices = data.devices || [];
      const careTeam = data.careTeam || {};
      const goalsOfCare = data.goalsOfCare || {};
      
      // Format patient name from userProfile
      const patientName = userProfile.fname && userProfile.lname 
        ? `${userProfile.fname} ${userProfile.lname}` 
        : userProfile.fname || "";
      
      // Format DOB (American date format MM/DD/YYYY)
      const dob = userProfile.bday 
        ? moment(userProfile.bday).format("MM/DD/YYYY") 
        : "";
      
      // Format consent date (American date format MM/DD/YYYY) — falls back to
      // carePlanCreatedDate (RPM ONB note date, or first chart note date) when no
      // verbal consent note exists.
      const consentDate = patientConsent.rpmVerbalNoteDate
        ? moment(patientConsent.rpmVerbalNoteDate).format("MM/DD/YYYY")
        : (data.carePlanCreatedDate ? moment(data.carePlanCreatedDate).format("MM/DD/YYYY") : "");
      
      // Format devices info
      // A replacement device adds a new record without removing the old one, so the
      // same device can repeat. Label each with its device type, since two different
      // devices can share a name, then collapse the identical labels.
      const deviceLabel = (d) => {
        const name = (d.device && d.device.name) || 'Device';
        const type = d.device && d.device.deviceType && d.device.deviceType.description;
        return type ? `${name} (${type})` : name;
      };
      const deviceInfo = devices.length > 0 
        ? [...new Set(devices.map(deviceLabel))].join(", ")
        : "";
      
       const baseImageUrl = process.env.REACT_APP_VIDEO_CALL_REQUEST;
      const patientSig = userProfile.patientSignature
        ? `${baseImageUrl}/${userProfile.patientSignature}`
        : undefined;
      const providerSig = userProfile.providerSignature
        ? `${baseImageUrl}/${userProfile.providerSignature}`
        : undefined;
      const medicalDirectorSig = data.medicalDirectorSignature
        ? `${baseImageUrl}/${data.medicalDirectorSignature}`
        : undefined;

      const dateRPMInitiated = data.carePlanCreatedDate
        ? moment(data.carePlanCreatedDate).format("MM/DD/YYYY")
        : (consentDate || "");
      // Real last-modified date when the plan has been updated; otherwise the plan
      // has never been modified since creation, so it matches the created date.
      const lastModifiedDate = data.lastModifyDate
        ? moment(data.lastModifyDate).format("MM/DD/YYYY")
        : dateRPMInitiated;

      setFormData({
        patientName: patientName,
        dob: dob,
        primaryDiagnosis: userProfile.primaryDiagnosis || "",
        orderingProvider: userProfile.mainCareProvider || "",
        npi: userProfile.NPI && userProfile.NPI !== "N/A" ? userProfile.NPI : "",
        dateRPMInitiated: dateRPMInitiated,
        lastModifiedDate: lastModifiedDate,
        // Welcome Call Completed note date, or the patient's first upload date for
        // patients enrolled before that note existed. Computed by the API.
        setupEducationDate: data.setupEducationDate ? moment(data.setupEducationDate).format("MMMM DD, YYYY") : "",
        medicalNecessity: data.medicalNecessity === true,
        rpmRationale: patientExpectations.rationaleRPM || false,
        consentDate: consentDate,
        deviceInfo: deviceInfo,
        careTeam: careTeam,
        escalationPlan: data.escalationPlan || {},
        goalsOfCare: goalsOfCare,
        patientSignature: patientSig,
        providerSignature: providerSig,
        providerName: userProfile.mainCareProvider || "",
        medicalDirectorName: data.medicalDirectorName || "",
        medicalDirectorNPI: data.medicalDirectorNPI || "",
        medicalDirectorSignature: medicalDirectorSig,
        hasVerbalConsentNote: !!data.hasVerbalConsentNote,
        expectationThreshold: isEditing ? [] : (patientExpectations.expectationThreshold || []),
        conditionNames: conditionNames,
        // rpmCarePlanInfo (used when isEditing) never carries conditionIcdCodes — it's built
        // from the patient profile, not the saved RPMCarePlan doc — so always read this from
        // rpmCarePlanDetailsInfo, which is fetched by rpmCarePlanId regardless of edit mode.
        conditionIcdCodes: (rpmCarePlanDetailsInfo && rpmCarePlanDetailsInfo.conditionIcdCodes) || [],
        rpmMedicalNecessityConfig: isEditing ? [] : (data.rpmMedicalNecessity || []),
        savedConditionIds: isEditing ? [] : (data.conditionIds || []),
        selectedConditions: isEditing ? [] : (data.conditionIds || [])
      });

      // Set RPM medical necessity config from data
      if (data.rpmMedicalNecessity && Array.isArray(data.rpmMedicalNecessity)) {
        setRpmMedicalNecessityConfig(data.rpmMedicalNecessity);
      }

      // Resolve condition names from populated conditionIds
      if (data.conditionIds && Array.isArray(data.conditionIds) && data.conditionIds.length > 0) {
        // New records: conditionIds are populated objects with condition field
        const names = data.conditionIds
          .map(c => {
            if (typeof c === 'object' && c !== null && c.condition) {
              return c.description
                ? `${c.condition} (${c.description})`
                : c.condition;
            }
            return null;
          })
          .filter(Boolean);

        if (names.length > 0) {
          setConditionNames(names);
        } else {
          // conditionIds exist but populate returned raw ObjectIds (ref mismatch)
          // fall back to rpmPatientConditions from Redux
          const fallbackNames = rpmPatientConditions.map(c =>
            c.description ? `${c.condition} (${c.description})` : c.condition
          );
          setConditionNames(fallbackNames);
        }
      } else if (rpmPatientConditions && rpmPatientConditions.length > 0) {
        // Old records: conditionIds was never saved — show all patient conditions as fallback
        const fallbackNames = rpmPatientConditions.map(c =>
          c.description ? `${c.condition} (${c.description})` : c.condition
        );
        setConditionNames(fallbackNames);
      }
      // Set the selected condition and expectations from saved data
      if (!(isEditing)) {
        if (data.conditionIds) {
          setSelectedConditionIds(data.conditionIds);
        }
      }
      if (rpmCarePlanDetailsInfo && Array.isArray(rpmCarePlanDetailsInfo.conditionIcdCodes)) {
        setSelectedConditionIcdCodes(rpmCarePlanDetailsInfo.conditionIcdCodes);
      }

      if (data.patientExpectations.expectationThreshold && Array.isArray(data.patientExpectations.expectationThreshold)) {
        setSelectedExpectations(data.patientExpectations.expectationThreshold);
      }
    }
  }, [rpmCarePlanDetailsInfo, rpmCarePlanInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle condition change - fetch medical necessity config when conditions change
  const handleConditionChange = (selectedConditions) => {
    let conditionIds = [];
    if (Array.isArray(selectedConditions)) {
      conditionIds = selectedConditions.map(c => c._id || c);
      setSelectedConditionIds(conditionIds);
    } else if (selectedConditions && selectedConditions._id) {
      conditionIds = [selectedConditions._id];
      setSelectedConditionIds(conditionIds);
    } else if (typeof selectedConditions === 'string') {
      conditionIds = [selectedConditions];
      setSelectedConditionIds(conditionIds);
    } else {
      setSelectedConditionIds([]);
    }
    console.log("Selected condition IDs:" + conditionIds);
    // Fetch medical necessity config when conditions change
    if (conditionIds.length > 0 && patientId) {
      dispatch({
        type: "DO_GET_RPM_MEDICAL_NECESSITY_CONFIG",
        payload: { conditionIds: conditionIds.join(','), patientId }
      });
    }
  };

  const handleConditionIcdCodesChange = (rows) => {
    setSelectedConditionIcdCodes(rows);
  };

  // Fetch expectations and medical necessity config when conditions are loaded/selected
  useEffect(() => {
    if (selectedConditionIds && selectedConditionIds.length > 0) {
      console.log("Fetching medical necessity config for condition IDs:" + selectedConditionIds.map(id => id._id || id).join(','));
      dispatch({
        type: "DO_GET_RPM_MEDICAL_NECESSITY_CONFIG",
        payload: { conditionIds: selectedConditionIds.map(id => id._id || id).join(','), patientId }
      });
    }
  }, [selectedConditionIds, isEditing]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // This is a view component, so submit might not be needed
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="rpm-care-plan-view">
      {/* LottieLoader at top of view */}
      {(ongoingRPMCarePlanRequest || ongoingRPMCarePlanDetailsRequest) && (
        <div className="text-center" style={{ marginTop: "50px", marginBottom: "50px" }}>
          <LottieLoader
            loadLoop={true}
            source={lottieLoaderFile}
            Lheight={150}
            Lwidth={150}
            subTitle={"Please Wait ..."}
          />
        </div>
      )}
      
      {!ongoingRPMCarePlanRequest && !ongoingRPMCarePlanDetailsRequest && !rpmCarePlanInfo && !rpmCarePlanDetailsInfo && (
        <div className="text-center my-3">
          <p>No RPM Care Plan information found for this patient.</p>
        </div>
      )}
      
      {!ongoingRPMCarePlanRequest && !ongoingRPMCarePlanDetailsRequest && rpmCarePlanDetailsInfo && (
        <RPMCarePlanForm
          ref={carePlanFormRef}
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={isEditing ? handleSave : handleSubmit}
          isEditing={isEditing}
          showButtons={isEditing}
          patientId={patientId}
          onExpectationsChange={(exps) => {
            setSelectedExpectations(exps);
            if (isEditing) {
              setFormData(prev => ({ ...prev, expectationThreshold: exps }));
            }
          }}
          onConditionChange={handleConditionChange}
          onConditionIcdCodesChange={handleConditionIcdCodesChange}
          onConditionIcdCodesValidityChange={setIsIcdCodesValid}
          isViewMode={!isEditing}
          conditionNames={conditionNames}
          savedConditionIds={selectedConditionIds}
          savedConditionIcdCodes={selectedConditionIcdCodes}
          showConditionError={formData.showConditionError}
          showIcdCodesError={showIcdCodesError}
          rpmMedicalNecessityConfig={rpmMedicalNecessityConfigFromRedux.length > 0 ? rpmMedicalNecessityConfigFromRedux : rpmMedicalNecessityConfig}
        />
      )}

      {/* Hidden Print Component */}
      <div style={{ display: 'none' }}>
        <RPMCarePlanPrint
          ref={printRef}
          formData={formData}
          rpmMedicalNecessityConfig={rpmMedicalNecessityConfigFromRedux.length > 0 ? rpmMedicalNecessityConfigFromRedux : rpmMedicalNecessityConfig}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    profile: state.user.profileOwner
  };
};

export default connect(mapStateToProps, null, null, { forwardRef: true })(React.forwardRef(ViewRPMCarePlan));